// <product-configurator>: reads the option controls rendered by snippets/configurator-*.liquid,
// keeps the price and live preview in step, and adds the gift (plus chosen add-ons) to the bag.
// Option groups are data the owner edits in Shopify admin; this file never needs to know a product.

import { addItems } from '@theme/cart-api';
import { $, $$, formatPrice, randomId, toast } from '@theme/utils';

class ProductConfigurator extends HTMLElement {
  connectedCallback() {
    this.data = JSON.parse($('script[data-product-json]', this).textContent);
    // The sticky phone bar sits outside this element but inside the same Shopify section.
    this.scope = this.closest('.shopify-section') || document;
    this.form = $('form', this);
    this.form.addEventListener('change', () => this.update());
    this.form.addEventListener('input', (event) => this.onInput(event));
    this.form.addEventListener('submit', (event) => this.submit(event));
    $$('[data-quantity-step]', this).forEach((b) => b.addEventListener('click', () => this.stepQuantity(Number(b.dataset.quantityStep))));
    $$('[data-submit-proxy]', this.scope).forEach((b) => b.addEventListener('click', () => this.form.requestSubmit()));
    this.update();
  }

  get quantity() {
    return Math.max(1, Number($('[name="quantity"]', this.form)?.value) || 1);
  }

  stepQuantity(step) {
    const input = $('[name="quantity"]', this.form);
    input.value = Math.max(1, Math.min(20, this.quantity + step));
    this.update();
  }

  get variant() {
    const chosen = this.data.options.map((option, i) => $(`[name="option-${i}"]:checked`, this.form)?.value ?? option.values[0]);
    return this.data.variants.find((v) => v.options.every((value, i) => value === chosen[i])) || this.data.variants[0];
  }

  /** Values of every option group, keyed by group id. */
  readGroups() {
    return this.data.groups.map((group) => {
      const scope = $(`[data-group="${group.id}"]`, this.form);
      if (!scope) return { group, value: null };
      switch (group.kind) {
        case 'text':
          return { group, value: $('input, textarea', scope).value.trim() };
        case 'palette': {
          const index = $('input:checked', scope)?.value;
          return { group, value: index === undefined ? null : group.palettes[Number(index)] };
        }
        case 'choice':
          return { group, value: $('input:checked', scope)?.value || '' };
        case 'addons':
        case 'picks':
          return { group, value: $$('input:checked', scope).map((input) => group.products[Number(input.value)]) };
        default:
          return { group, value: null };
      }
    });
  }

  onInput(event) {
    const field = event.target.closest('[data-count-for]') ? event.target : null;
    const counter = field && $(`[data-counter="${field.dataset.countFor}"]`, this.form);
    if (counter) counter.textContent = `${field.value.length}/${field.maxLength}`;
    if (event.target.closest('[data-group]')) this.update();
  }

  update() {
    const variant = this.variant;
    const values = this.readGroups();
    const extras = values.filter((v) => v.group.kind === 'addons').flatMap((v) => v.value || []);
    const total = (variant.price + extras.reduce((sum, p) => sum + p.price, 0)) * this.quantity;

    $$('[data-total]', this.scope).forEach((el) => { el.textContent = formatPrice(total); });
    $$('[data-variant-price]', this).forEach((el) => { el.textContent = formatPrice(variant.price); });
    $$('[data-variant-compare]', this).forEach((el) => {
      el.hidden = !(variant.compare_at > variant.price);
      el.textContent = formatPrice(variant.compare_at);
    });
    $$('[data-sale-tag]', this).forEach((el) => { el.hidden = !(variant.compare_at > variant.price); });
    $$('[data-add-label]', this.scope).forEach((el) => { el.textContent = variant.available ? el.dataset.available : el.dataset.soldOut; });
    $$('[data-add]', this.scope).forEach((el) => el.toggleAttribute('disabled', !variant.available));
    const idInput = $('[name="id"]', this.form);
    if (idInput) idInput.value = variant.id;

    // Picks: stop at the maximum.
    values.filter((v) => v.group.kind === 'picks' && v.group.max).forEach(({ group, value }) => {
      const scope = $(`[data-group="${group.id}"]`, this.form);
      $$('input:not(:checked)', scope).forEach((input) => { input.disabled = value.length >= group.max || !group.products[Number(input.value)].available; });
      const status = $('[data-picks-status]', scope);
      if (status) status.textContent = `${value.length} of ${group.max} chosen`;
    });

    this.emitDesign(values, variant);
  }

  emitDesign(values, variant) {
    const text = values.find((v) => v.group.kind === 'text' && v.group.preview);
    const palette = values.find((v) => v.group.kind === 'palette')?.value;
    const lettering = values.find((v) => v.group.kind === 'choice' && v.group.preview)?.value;
    $$('[data-palette-name]', this).forEach((el) => { el.textContent = palette?.name || ''; });
    // Kept on the element so a gallery that upgrades later can catch up (module load order isn't fixed).
    this.design = {
      hasPreview: Boolean(text),
      text: text?.value || '',
      colours: palette?.colours,
      ribbon: palette?.ribbon,
      box: palette?.box,
      lettering,
      variantImage: variant.image,
      source: document.activeElement?.closest?.('[data-group]') ? 'option' : 'other',
    };
    this.dispatchEvent(new CustomEvent('design:change', { bubbles: true, detail: this.design }));
  }

  validate(values) {
    const errors = [];
    values.forEach(({ group, value }) => {
      const scope = $(`[data-group="${group.id}"]`, this.form);
      const hint = scope && $('[data-error]', scope);
      let message = '';
      if (group.required && (value === null || value === '' || (Array.isArray(value) && !value.length))) {
        // Labels often start with "Your …" already ("Your bubble message"), so don't say "your your".
        const name = group.label.toLowerCase().replace(/^your\s+/, '');
        message = group.kind === 'text' ? `Add your ${name}.` : `Choose your ${name}.`;
      }
      if (group.kind === 'picks' && Array.isArray(value) && group.min && value.length < group.min) {
        message = `Choose ${group.min - value.length} more.`;
      }
      if (hint) {
        hint.textContent = message;
        hint.hidden = !message;
      }
      if (message) errors.push(scope);
    });
    return errors;
  }

  /** Line item properties for the gift itself; underscore keys are hidden from shoppers. */
  properties(values, bundle) {
    const props = { _bundle: bundle };
    values.forEach(({ group, value }) => {
      if (group.kind === 'addons' || value === null || value === '' || (Array.isArray(value) && !value.length)) return;
      if (group.kind === 'palette') {
        props[group.property] = value.name;
        props._colours = value.colours.join(',');
      } else if (group.kind === 'picks') {
        props[group.property] = value.map((p) => p.title).join(', ');
      } else {
        props[group.property] = value;
      }
    });
    return props;
  }

  async submit(event) {
    event.preventDefault();
    const variant = this.variant;
    if (!variant.available) return;
    const values = this.readGroups();
    const errors = this.validate(values);
    if (errors.length) {
      errors[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      $('input, textarea', errors[0])?.focus({ preventScroll: true });
      return;
    }
    const bundle = randomId();
    const quantity = this.quantity;
    const addons = values.filter((v) => v.group.kind === 'addons').flatMap((v) => v.value);
    const items = [
      { id: variant.id, quantity, properties: this.properties(values, bundle) },
      ...addons.map((p) => ({ id: p.id, quantity, properties: { _bundle: bundle, _addon_for: this.data.product.title } })),
    ];
    const buttons = $$('[data-add]', this.scope);
    buttons.forEach((b) => b.classList.add('is-loading'));
    try {
      await addItems(items);
    } catch (error) {
      toast(error.message);
    } finally {
      buttons.forEach((b) => b.classList.remove('is-loading'));
    }
  }
}

customElements.define('product-configurator', ProductConfigurator);
